import { Navigate, useLocation } from "react-router-dom";
import { useAuth, userMode } from "@/context/AuthContext";

// Gate for every /lister* route. Signed-out users go log in; signed-in users
// who are NOT in lister mode are sent to the "Become a lister" interstitial
// (carrying where they were headed), which flips them into lister mode and
// then continues. Admins always pass. Backend enforces the same rule on
// listing writes (403 MODE_REQUIRED), so this is UX, not the security boundary.
export default function RequireLister({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
  }

  const isLister = user.role === "admin" || userMode(user) === "lister";
  if (!isLister) {
    // A realtor whose application is not approved yet cannot enter the lister
    // surface. Send them to their status page (Settings) rather than back into
    // onboarding, so they see the honest "pending review" / "rejected" state.
    if (user.lister_kind === "realtor" && user.realtor_status !== "approved") {
      return <Navigate to="/profile?realtor=1" replace />;
    }
    // A renter viewing a lister route (most commonly right after switching to
    // renter mode while on the dashboard) simply belongs in the renter home,
    // NOT the "Become a lister" chooser. Sending them to the chooser was the
    // cause of the "switched to renter but see Private/Realtor page" bug.
    // Onboarding is reached only through the explicit "List your place" action.
    return <Navigate to="/" replace />;
  }

  return children;
}
